<template>
  <Modal
    :show="showModal"
    :title="t('Escolha um Template de Mensagem')"
    size="modalMd"
    :close-label="t('Cancelar')"
    :hide-save-button="true"
    @close="handleClose"
    @dismiss="handleClose"
  >
    <!-- Loading State -->
    <div v-if="isLoading" class="loading-state">
      <ProgressSpinner style="width: 3rem; height: 3rem" strokeWidth="6" />
      <p class="mt-3">{{ t('Carregando templates...') }}</p>
    </div>

    <!-- No Templates -->
    <div v-else-if="templates.length === 0" class="empty-state">
      <i class="pi pi-inbox empty-state-icon"></i>
      <h3>{{ t('Nenhum Template Disponível') }}</h3>
      <p>{{ t('Não há templates cadastrados para as franquias selecionadas.') }}</p>
    </div>

    <!-- Templates Table -->
    <div v-else class="template-selection-container">
      <!-- Search Bar -->
      <div class="search-bar">
        <div class="search-field">
          <i class="pi pi-search search-prefix-icon"></i>
          <input-text
            v-model="searchQuery"
            elem-id="mensagens_programadas_searchQuery"
            :placeholder="t('Buscar por nome ou conteúdo...')"
            data-testid="mensagens-template-search-input"
            class="w-full search-input"
          />
        </div>
      </div>

      <!-- No Results -->
      <div v-if="filteredTemplates.length === 0" class="no-results">
        <i class="pi pi-search no-results-icon"></i>
        <h3>{{ t('Nenhum Template Encontrado') }}</h3>
        <p>{{ t('Tente ajustar sua busca ou filtros.') }}</p>
        <Button
          :label="t('Limpar Filtros')"
          icon="pi pi-filter-slash"
          text
          data-testid="mensagens-template-clear-filters-btn"
          @click="clearFilters"
        />
      </div>

      <!-- Templates DataTable -->
      <data-table
        v-else
        :config="templatesTableConfig"
        :data="filteredTemplates"
        @button-click="handleButtonClick"
      />
    </div>
  </Modal>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import Modal from '@/ui/Modal.vue';
import { log } from '@/utils/DebugLogger';
import { eventBus } from '@/eventBus';
import { useMensagensProgramadas } from '@/composables/useMensagensProgramadas';
import { permissionsService } from '@/services/permissionsService';

interface Template {
  template_id: number | string;
  template_name?: string;
  template_content?: string;
  franquia_id?: number | string;
  franquia_nome?: string;
  [key: string]: unknown;
}

interface CellParams {
  data: Template & { _isFrozenFooter?: boolean };
  value?: unknown;
}

interface ButtonClickPayload {
  value: number | string | null;
  data: Template;
  action: string;
}

interface OpenSelectionPayload {
  franchiseIds?: Array<number | string>;
  callback?: (template: Template) => void;
}

interface FranchiseOption {
  ID: number | string;
  Name: string;
}

const { t } = useI18n();
const { getTemplatesByFranchises } = useMensagensProgramadas();

// Data
const showModal = ref(false);
const isLoading = ref(false);
const templates = ref<Template[]>([]);
const selectedTemplate = ref<Template | null>(null);
const franchiseIds = ref<Array<number | string>>([]);
const callbackFn = ref<((template: Template) => void) | null>(null);
const searchQuery = ref('');

// Computed
const filteredTemplates = computed<Template[]>(() => {
  let filtered = templates.value;

  // Filter by search query
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase();
    filtered = filtered.filter(template =>
      template.template_name?.toLowerCase().includes(query) ||
      template.template_content?.toLowerCase().includes(query) ||
      template.franquia_nome?.toLowerCase().includes(query)
    );
  }

  return filtered;
});

const escapeHtml = (str: string): string => {
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
};

const templatesTableConfig = computed(() => ({
  tableId: 'mensagensTemplateSelectionTable',
  pagination: false,
  columns: [
    {
      data: 'template_id',
      title: t('ID'),
      width: 80,
      cellRenderer: (params: CellParams) => {
        const id = escapeHtml(String(params.data.template_id ?? ''));
        return `<span class="template-id-cell">#${id}</span>`;
      }
    },
    {
      data: 'template_name',
      title: t('Nome do Template'),
      sortable: true,
      width: 200,
      cellRenderer: (params: CellParams) => {
        const name = escapeHtml(params.data.template_name || '');
        return `<div class="template-name-cell"><i class="pi pi-file-edit template-icon"></i><span class="template-name-text">${name}</span></div>`;
      }
    },
    {
      data: 'template_content',
      title: t('Conteúdo'),
      width: 350,
      cellRenderer: (params: CellParams) => {
        const content = escapeHtml(params.data.template_content || '');
        return `<span class="content-truncated">${content}</span>`;
      }
    },
    {
      data: 'template_id',
      title: t('Ação'),
      width: 120,
      sortable: false,
      renderType: 'clickable_button',
      buttonText: t('Selecionar'),
      buttonIcon: 'pi pi-check',
      buttonClass: 'p-button-success p-button-sm',
      buttonAction: 'select',
      buttonTestId: 'mensagens-template-select-btn'
    }
  ]
}));

// Methods
const openModal = (payload?: OpenSelectionPayload): void => {
  log('MessageTemplateSelectionModal', 'openModal', 'Opening modal with payload', payload);

  showModal.value = true;
  franchiseIds.value = payload?.franchiseIds || [];
  callbackFn.value = payload?.callback || null;

  // Reset search
  searchQuery.value = '';

  void loadTemplates();
};

const handleClose = (): void => {
  log('MessageTemplateSelectionModal', 'handleClose', 'Closing modal');
  showModal.value = false;
  selectedTemplate.value = null;
  templates.value = [];
  callbackFn.value = null;
  searchQuery.value = '';
};

const loadTemplates = async (): Promise<void> => {
  log('MessageTemplateSelectionModal', 'loadTemplates', 'Loading templates for franchises', franchiseIds.value);

  isLoading.value = true;

  try {
    const backendTemplates = (await getTemplatesByFranchises(franchiseIds.value)) as unknown as Template[];

    // Get franchise names from permissions service
    const franchisesMap: Record<string, string> = {};
    const franchises = (permissionsService.getFranquiasSelectArray() || []) as FranchiseOption[];
    franchises.forEach(f => {
      franchisesMap[String(f.ID)] = f.Name;
    });

    // Map templates with franchise names
    templates.value = (backendTemplates || []).map(template => ({
      ...template,
      franquia_nome:
        (template.franquia_id != null && franchisesMap[String(template.franquia_id)]) ||
        template.franquia_nome ||
        t('Geral')
    }));

    log('MessageTemplateSelectionModal', 'loadTemplates', 'Loaded templates', templates.value.length);
    log('MessageTemplateSelectionModal', 'loadTemplates', 'Templates with franchises', templates.value);
  } catch (error) {
    log('MessageTemplateSelectionModal', 'loadTemplates', 'Error loading templates', error);
    templates.value = [];
    eventBus.emit('globalMessage.error', t('Erro ao carregar templates'));
  } finally {
    isLoading.value = false;
  }
};

const handleConfirm = (): void => {
  if (!selectedTemplate.value) {
    return;
  }

  log('MessageTemplateSelectionModal', 'handleConfirm', 'Confirming template selection', selectedTemplate.value);

  // Call the callback function if provided
  if (callbackFn.value && typeof callbackFn.value === 'function') {
    callbackFn.value(selectedTemplate.value);
  }

  handleClose();
};

const clearFilters = (): void => {
  searchQuery.value = '';
};

const selectTemplate = (template: Template): void => {
  log('MessageTemplateSelectionModal', 'selectTemplate', 'Template selected', template);
  selectedTemplate.value = template;
  handleConfirm();
};

const handleButtonClick = ({ data, action }: ButtonClickPayload): void => {
  if (action === 'select') {
    selectTemplate(data);
  }
};

// Event Handlers
const handleTemplateSelectionRequest = (payload?: OpenSelectionPayload): void => {
  log('MessageTemplateSelectionModal', 'handleTemplateSelectionRequest', 'Template selection requested', payload);
  openModal(payload);
};

// Lifecycle
onMounted(() => {
  log('MessageTemplateSelectionModal', 'onMounted', 'Component mounted');
  eventBus.on('template.open-selection', handleTemplateSelectionRequest);
});

onBeforeUnmount(() => {
  log('MessageTemplateSelectionModal', 'onBeforeUnmount', 'Component unmounting');
  eventBus.off('template.open-selection', handleTemplateSelectionRequest);
});
</script>

<style scoped>
/* Container */
.template-selection-container {
  padding: 1rem 0;
}

/* Loading State */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
  gap: 1rem;
  color: var(--text-color-secondary);
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
  text-align: center;
}

.empty-state-icon {
  font-size: 4rem;
  color: var(--text-color-secondary);
  opacity: 0.5;
  margin-bottom: 1rem;
}

.empty-state h3 {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-color);
  margin: 0 0 0.5rem 0;
}

.empty-state p {
  font-size: 0.95rem;
  color: var(--text-color-secondary);
  margin: 0;
}

/* Search Bar */
.search-bar {
  margin-bottom: 2rem;
  padding: 1.25rem;
  background: #f9fafb;
  border-radius: 12px;
  border: 2px solid #e5e7eb;
}

.search-field {
  width: 100%;
  position: relative;
  display: flex;
  align-items: center;
}

.search-prefix-icon {
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: #6b7280;
  pointer-events: none;
  z-index: 1;
}

.search-field :deep(.input-text-wrapper) {
  width: 100%;
  margin-bottom: 0;
}

.search-field :deep(.p-inputtext) {
  font-size: 1rem;
  padding: 0.75rem 1rem 0.75rem 2.75rem;
  border-radius: 8px;
  border: 2px solid #e5e7eb;
  transition: all 0.2s ease;
  width: 100%;
}

.search-field :deep(.p-inputtext):focus {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

/* No Results */
.no-results {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
  text-align: center;
}

.no-results-icon {
  font-size: 3rem;
  color: var(--text-color-secondary);
  opacity: 0.5;
  margin-bottom: 1rem;
}

.no-results h3 {
  font-size: 1.15rem;
  font-weight: 600;
  color: var(--text-color);
  margin: 0 0 0.5rem 0;
}

.no-results p {
  font-size: 0.9rem;
  color: var(--text-color-secondary);
  margin: 0 0 1rem 0;
}

/* Templates Table */
.templates-table {
  margin-top: 1rem;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.templates-table :deep(.p-datatable-thead > tr > th) {
  background: #f9fafb;
  color: #374151;
  font-weight: 700;
  font-size: 0.95rem;
  padding: 1rem;
  border-bottom: 2px solid #e5e7eb;
}

.templates-table :deep(.p-datatable-tbody > tr) {
  transition: all 0.2s ease;
}

.templates-table :deep(.p-datatable-tbody > tr > td) {
  padding: 1rem;
  vertical-align: middle;
}

.templates-table :deep(.p-datatable-tbody > tr:hover) {
  background: #f0fdf4;
  transform: scale(1.01);
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.15);
}

/* Template ID Cell */
.template-id-cell {
  font-family: 'Courier New', monospace;
  font-weight: 600;
  color: #9ca3af;
  background: #f3f4f6;
  padding: 0.25rem 0.6rem;
  border-radius: 6px;
  font-size: 0.875rem;
}

/* Template Name Cell */
.template-name-cell {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.template-icon {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  flex-shrink: 0;
}

.template-name-text {
  font-weight: 600;
  color: #111827;
  font-size: 1rem;
}

/* Template Content Cell */
.content-truncated {
  white-space: pre-wrap;
  word-break: break-word;
  color: #6b7280;
  font-size: 0.95rem;
  line-height: 1.5;
}

/* Table Empty State */
.table-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
  text-align: center;
  color: #9ca3af;
}

.table-empty-state i {
  font-size: 4rem;
  opacity: 0.5;
  margin-bottom: 1rem;
}

.table-empty-state h3 {
  font-size: 1.25rem;
  font-weight: 600;
  color: #6b7280;
  margin: 0 0 0.5rem 0;
}

.table-empty-state p {
  font-size: 0.95rem;
  color: #9ca3af;
  margin: 0;
}

/* Utility Classes */
.w-full {
  width: 100%;
}

.text-center {
  text-align: center;
}

.mt-3 {
  margin-top: 1rem;
}

/* Responsive */
@media (max-width: 768px) {
  .search-bar {
    padding: 1rem;
  }

  .templates-table :deep(.p-datatable-thead > tr > th) {
    font-size: 0.875rem;
    padding: 0.75rem;
  }

  .templates-table :deep(.p-datatable-tbody > tr > td) {
    padding: 0.75rem;
  }

  .template-icon {
    width: 32px;
    height: 32px;
    font-size: 0.9rem;
  }

  .template-name-text {
    font-size: 0.9rem;
  }

  .content-truncated {
    font-size: 0.875rem;
  }
}
</style>
